/**
 * Panel Layout Store - IndexedDB persistence for spatial canvas layouts
 * Stores panel positions, sizes, and Z-index per user
 */

import type { UserId } from '@maco144/shared';

export interface PanelState {
  id: string;
  type: PanelType;
  position: { x: number; y: number };
  size: { width: number; height: number };
  zIndex: number;
  minimized: boolean;
  props?: Record<string, any>;
}

export type PanelType =
  | 'discord'
  | 'x'
  | '3d'
  | 'spatial-grid'
  | 'settings'
  | 'identity'
  | 'linked-identities'
  | 'business'
  | 'private-chat'
  | 'receipts'
  | 'metrics'
  | 'pieces'
  | 'troves'
  | 'canvas'
  | 'objects'
  | 'world-map'
  | 'voice'
  | 'my-accords'
  | 'public-profile'
  | 'accord-detail'
  | 'preferences'
  | 'groups'
  | 'feed'
  | 'topics'
  | 'meeting-notes'
  | 'avid-player'
  | 'avid-channels'
  | 'avid-publish'
  | 'review-receipt';

export interface PanelLayout {
  userId: UserId;
  panels: PanelState[];
  nextZIndex: number;
  gridSize: number;
  savedAt: number;
}

/**
 * IndexedDB store for panel layouts
 * Per-user isolation: rising-sun-panel-layout-${userId}
 */
export class PanelLayoutStore {
  private dbName: string;
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  constructor(userId: UserId) {
    this.dbName = `rising-sun-panel-layout-${userId}`;
  }

  /**
   * Initialize the database
   */
  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains('layouts')) {
          db.createObjectStore('layouts', { keyPath: 'id' });
        }
      };
    });
  }

  /**
   * Get a store from the database
   */
  private getStore(storeName: string, mode: IDBTransactionMode): IDBObjectStore {
    if (!this.db) throw new Error('Database not initialized');
    const tx = this.db.transaction(storeName, mode);
    return tx.objectStore(storeName);
  }

  /**
   * Save a layout
   */
  async saveLayout(layout: PanelLayout): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('layouts', 'readwrite');
      const request = store.put({
        id: 'current',
        ...layout,
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Load the current layout
   */
  async loadLayout(): Promise<PanelLayout | null> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('layouts', 'readonly');
      const request = store.get('current');
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            userId: result.userId,
            panels: result.panels || [],
            nextZIndex: result.nextZIndex || 1000,
            gridSize: result.gridSize || 20,
            savedAt: result.savedAt || 0,
          });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all layouts
   */
  async clear(): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('layouts', 'readwrite');
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Close the database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}
